import type { z } from 'zod';

import type {
  CategorizedPreference,
  PreferenceCategory,
  ProfilePreferencesSchema,
  TaggedPreference,
} from './schemas/shared-schemas.js';

/** One entry of a `preferences` list: categorized, or a legacy `{ value, source }` not yet migrated. */
export type ProfilePreference = NonNullable<z.infer<typeof ProfilePreferencesSchema>>[number];

/**
 * The per-topic lists that predate categorized `preferences`, and the category each folds into.
 * `travelCreditCards` is deliberately absent: it's a separate fact, not a preference.
 */
export const LEGACY_PREFERENCE_FIELDS = {
  preferredAirports: 'airport',
  hotelPreferences: 'hotel',
  wishlistDestinations: 'destination',
  keyDates: 'key_date',
} as const satisfies Record<string, PreferenceCategory>;
export type LegacyPreferenceField = keyof typeof LEGACY_PREFERENCE_FIELDS;

/** The preference-bearing part of a member's `travelerProfile` or a companion. */
export type PreferenceHolder = { preferences?: ProfilePreference[] } & {
  [Field in LegacyPreferenceField]?: TaggedPreference[];
};

export function isCategorizedPreference(entry: ProfilePreference): entry is CategorizedPreference {
  return 'id' in entry && typeof entry.id === 'string' && 'category' in entry;
}

/** Comparison key for "the same fact": case, width and whitespace differences don't count. */
export function normalizePreferenceValue(value: string): string {
  return value.normalize('NFKC').trim().replaceAll(/\s+/gu, ' ').toLocaleLowerCase();
}

/**
 * Folds a person's preferences into one categorized list: every entry gets an id and a category, and
 * the legacy per-topic lists (`LEGACY_PREFERENCE_FIELDS`) are merged in. Duplicates (same normalized
 * text) are dropped, keeping the first.
 *
 * `incoming` is what a client just sent (a whole-profile save). The merge exists so that save can't
 * erase what it never saw:
 * 1. Incoming entries that already have an id, in the client's order — but as stored, when stored:
 *    saved entries change only through the per-entry flows, which keep an "avoid" entry's hidden
 *    reference matching its text. A whole-profile save can't reword, recategorize or re-source one.
 * 2. Stored categorized entries the client didn't send — e.g. a fact Elara remembered while the form
 *    was open. Removal is a separate, per-entry operation, so absence here never means "delete".
 * 3. The legacy per-topic lists, stored and incoming, each into its category — ahead of plain
 *    entries, so a fact listed both ways keeps the category the per-topic list gave it.
 * 4. Incoming entries without an id — newly typed, or rebuilt from text by an older editor; they
 *    become `general` (and lose to an equal stored entry, so the stored id and metadata survive).
 * 5. Stored entries without an id — only when the client didn't send `preferences` at all; if it did,
 *    leaving an unsaved legacy entry out was its edit.
 *
 * With no `incoming`, it's a plain migration of `stored`.
 */
export function foldProfilePreferences({
  stored,
  incoming,
  newId,
}: {
  stored: PreferenceHolder | undefined;
  incoming?: PreferenceHolder;
  newId: () => string;
}): CategorizedPreference[] {
  const result: CategorizedPreference[] = [];
  const seenValues = new Set<string>();
  const add = (entry: CategorizedPreference) => {
    const key = normalizePreferenceValue(entry.value);
    if (!key || seenValues.has(key)) return;
    seenValues.add(key);
    result.push(entry);
  };
  const categorize = (entry: TaggedPreference, category: PreferenceCategory): CategorizedPreference => ({
    ...entry,
    id: newId(),
    category,
    source: entry.source ?? 'user',
  });

  const storedEntries = stored?.preferences ?? [];
  const incomingEntries = incoming?.preferences;
  const incomingIds = new Set(incomingEntries?.filter(isCategorizedPreference).map((entry) => entry.id));

  const storedById = new Map(storedEntries.filter(isCategorizedPreference).map((entry) => [entry.id, entry]));
  for (const entry of incomingEntries ?? []) {
    if (isCategorizedPreference(entry)) add(storedById.get(entry.id) ?? entry);
  }
  for (const entry of storedEntries) if (isCategorizedPreference(entry) && !incomingIds.has(entry.id)) add(entry);
  for (const [field, category] of Object.entries(LEGACY_PREFERENCE_FIELDS) as [
    LegacyPreferenceField,
    PreferenceCategory,
  ][]) {
    for (const entry of [...(stored?.[field] ?? []), ...(incoming?.[field] ?? [])]) add(categorize(entry, category));
  }
  for (const entry of incomingEntries ?? []) if (!isCategorizedPreference(entry)) add(categorize(entry, 'general'));
  if (!incomingEntries) {
    for (const entry of storedEntries) if (!isCategorizedPreference(entry)) add(categorize(entry, 'general'));
  }
  return result;
}

/** A copy of `holder` without the legacy per-topic lists — what's written once they're folded in. */
export function withoutLegacyPreferenceFields<T extends object>(holder: T): Omit<T, LegacyPreferenceField> {
  const copy = { ...holder } as Record<string, unknown>;
  for (const field of Object.keys(LEGACY_PREFERENCE_FIELDS)) delete copy[field];
  return copy as Omit<T, LegacyPreferenceField>;
}

/**
 * Travel credit cards offered as checkboxes — in onboarding, the member's settings and the admin console.
 * Any other card a member holds is kept as a free-text "other" entry. Elara uses these to find
 * cardholder-only perks. (`travelCreditCards` is its own field, not a categorized preference.)
 */
export const TRAVEL_CREDIT_CARD_OPTIONS = ['Amex Platinum', 'Chase Sapphire Reserve'] as const;

/** Whether a stored card entry is one of the {@link TRAVEL_CREDIT_CARD_OPTIONS} checkboxes. */
export function isListedTravelCreditCard(value: string): boolean {
  return (TRAVEL_CREDIT_CARD_OPTIONS as readonly string[]).includes(value);
}
